import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { PolygonElement } from '../elements/polygon-element';

export class ResizeManager extends ManagerBase {
    private isResizing: boolean = false;
    private activePolygon: PolygonElement | null = null;
    private activeLineIndex: number = -1;
    private startPoint: { x: number, y: number } | null = null;
    private lineDirection: { dx: number, dy: number } | null = null;
    private hoverLineIndex: number = -1;
    private handles: {
        lineIndex: number,
        x1: number, y1: number,
        x2: number, y2: number,
        handleX: number, handleY: number,
        handleWidth: number,
        handleHeight: number,
        angle: number
    }[] = [];

    constructor(levelDesigner: LevelDesignerComponent, canvas: HTMLCanvasElement) {
        super(levelDesigner, canvas);
        
        // Subscribe to state changes
        this.levelDesigner.stateChanged.subscribe(() => {
            this.calculateHandles();
        });
    }

    private calculateHandles(): void {
        this.handles = [];
        const selectedElements = this.levelDesigner.selectedElements;

        if (selectedElements.length === 1 && selectedElements[0] instanceof PolygonElement) {
            console.log('ResizeManager: Calculating handles');
            const polygon = selectedElements[0] as PolygonElement;
            const points = polygon.getPoints();
            const scale = this.levelDesigner.getScale();
            const handleThickness = 10 / scale; // 10px converted to world units
            const cornerGap = handleThickness; // Make corner gap match handle thickness

            for (let i = 0; i < points.length; i++) {
                const p1 = points[i];
                const p2 = points[(i + 1) % points.length];
                
                // Calculate line direction and length
                const dx = p2.x - p1.x;
                const dy = p2.y - p1.y;
                const length = Math.sqrt(dx * dx + dy * dy);
                const unitX = dx / length;
                const unitY = dy / length;
                
                // Calculate perpendicular direction
                const perpX = -unitY;
                const perpY = unitX;
                
                // Calculate handle corners with corner gaps
                const handleX = p1.x + (unitX * cornerGap);
                const handleY = p1.y + (unitY * cornerGap);
                const handleWidth = length - (2 * cornerGap);
                
                this.handles.push({
                    lineIndex: i,
                    x1: p1.x,
                    y1: p1.y,
                    x2: p2.x,
                    y2: p2.y,
                    handleX: handleX - (perpX * handleThickness/2),
                    handleY: handleY - (perpY * handleThickness/2),
                    handleWidth: handleWidth,
                    handleHeight: handleThickness,
                    angle: Math.atan2(dy, dx)
                });
            }
        }
    }

    public override handleMouseDown(event: MouseEvent): MouseEventResult | undefined {
        if (event.button === 1) return undefined;
        const mousePos = this.getMousePosition(event);
        this.calculateHandles();

        for (const handle of this.handles) {
            // Transform mouse point to handle's local coordinates
            const dx = mousePos.x - handle.handleX;
            const dy = mousePos.y - handle.handleY;
            const cos = Math.cos(-handle.angle);
            const sin = Math.sin(-handle.angle);
            
            // Rotate point to align with handle's coordinate system
            const localX = dx * cos - dy * sin;
            const localY = dx * sin + dy * cos;
            
            // Check if point is inside handle rectangle
            if (localX >= 0 && localX <= handle.handleWidth &&
                localY >= -handle.handleHeight/2 && localY <= handle.handleHeight/2) {
                this.isResizing = true;
                this.activePolygon = this.levelDesigner.selectedElements[0] as PolygonElement;
                this.activeLineIndex = handle.lineIndex;
                this.startPoint = mousePos;
                
                const dx = handle.x2 - handle.x1;
                const dy = handle.y2 - handle.y1;
                const length = Math.sqrt(dx * dx + dy * dy);
                this.lineDirection = { dx: -dy / length, dy: dx / length };
                
                return { cursor: this.getResizeCursor(this.activePolygon, this.activeLineIndex), stopPropagation: true, redraw: true };
            }
        }

        return undefined;
    }

    public override handleMouseMove(event: MouseEvent): MouseEventResult | undefined {
        const mousePos = this.getMousePosition(event);
        
        if (this.isResizing && this.activePolygon && this.startPoint && this.lineDirection) {
            console.log('ResizeManager: Active resize in progress');
            const dx = mousePos.x - this.startPoint.x;
            const dy = mousePos.y - this.startPoint.y;

            // Project the mouse movement onto the perpendicular direction
            const projectedDistance = dx * this.lineDirection.dx + dy * this.lineDirection.dy;

            this.activePolygon.resizeLineSegment(this.activeLineIndex, projectedDistance);
            this.levelDesigner.redraw();

            // Update the start point for the next move
            this.startPoint = mousePos;

            return { cursor: this.getResizeCursor(this.activePolygon, this.activeLineIndex), stopPropagation: true, redraw: true };
        } else {
            // No need to calculate handles here anymore since we're listening to state changes
            for (const handle of this.handles) {
                // Transform mouse point to handle's local coordinates
                const dx = mousePos.x - handle.handleX;
                const dy = mousePos.y - handle.handleY;
                const cos = Math.cos(-handle.angle);
                const sin = Math.sin(-handle.angle);
                
                // Rotate point to align with handle's coordinate system
                const localX = dx * cos - dy * sin;
                const localY = dx * sin + dy * cos;
                
                // Check if point is inside handle rectangle
                if (localX >= 0 && localX <= handle.handleWidth &&
                    localY >= -handle.handleHeight/2 && localY <= handle.handleHeight/2) {
                    console.log('ResizeManager: Hovering over handle');
                    const cursorStyle = this.getResizeCursor(this.levelDesigner.selectedElements[0] as PolygonElement, handle.lineIndex);
                    const needsRedraw = this.hoverLineIndex !== handle.lineIndex;
                    this.hoverLineIndex = handle.lineIndex;
                    return { cursor: cursorStyle, stopPropagation: false, redraw: needsRedraw };
                }
            }
            
            if (this.hoverLineIndex !== -1) {
                this.hoverLineIndex = -1;
                return { cursor: 'default', stopPropagation: false, redraw: true };
            }
        }

        return undefined;
    }

    public override handleMouseUp(event: MouseEvent): MouseEventResult | undefined {
        if (this.isResizing) {
            this.isResizing = false;
            this.activePolygon = null;
            this.activeLineIndex = -1;
            this.startPoint = null;
            this.lineDirection = null;
            this.levelDesigner.saveState();
            return { cursor: 'default', stopPropagation: true, redraw: true };
        }
        return undefined;
    }

    public override OnDraw(): void {
        if (this.levelDesigner.selectedElements.length === 0) return;

        const ctx = this.canvas.getContext('2d');
        if (!ctx) return;

        ctx.save();
        
        // Draw the highlighted line segment first
        if (!this.isResizing && this.hoverLineIndex !== -1) {
            const handle = this.handles[this.hoverLineIndex];
            const scale = this.levelDesigner.getScale();  // Get the current scale
            ctx.beginPath();
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 2 / scale;  // Match the line width from PolygonElement
            ctx.moveTo(handle.x1, handle.y1);
            ctx.lineTo(handle.x2, handle.y2);
            ctx.stroke();
        }
        
        // Draw handles
        //  for (const handle of this.handles) {
        //      ctx.save();
        //      ctx.fillStyle = handle.lineIndex === this.hoverLineIndex ? 'rgba(255, 0, 0, 0.5)' : 'rgba(255, 0, 0, 0.3)';
            
        //      ctx.translate(handle.handleX, handle.handleY);
        //      ctx.rotate(handle.angle);
        //      ctx.fillRect(0, -handle.handleHeight/2, handle.handleWidth, handle.handleHeight);
        //      ctx.restore();
        //  }

        ctx.restore();
    }

    private getResizeCursor(polygon: PolygonElement, lineIndex: number): string {
        const points = polygon.getPoints();
        const p1 = points[lineIndex];
        const p2 = points[(lineIndex + 1) % points.length];

        // Calculate the perpendicular direction
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        const perpDx = -dy / length;
        const perpDy = dx / length;

        // Determine the angle of the perpendicular direction
        const angle = Math.atan2(perpDy, perpDx);
        const degrees = angle * (180 / Math.PI);

        // Select the appropriate cursor based on the angle
        if (degrees >= -22.5 && degrees < 22.5) {
            return 'e-resize';
        } else if (degrees >= 22.5 && degrees < 67.5) {
            return 'se-resize';
        } else if (degrees >= 67.5 && degrees < 112.5) {
            return 's-resize';
        } else if (degrees >= 112.5 && degrees < 157.5) {
            return 'sw-resize';
        } else if (degrees >= 157.5 || degrees < -157.5) {
            return 'w-resize';
        } else if (degrees >= -157.5 && degrees < -112.5) {
            return 'nw-resize';
        } else if (degrees >= -112.5 && degrees < -67.5) {
            return 'n-resize';
        } else {
            return 'ne-resize';
        }
    }
}
